import * as IF from "infrontjs";

import { _lcs } from "../i18n";

// Registers the <app-layout> custom element (the guest shell). It in turn
// registers <bottom-nav> and <menu-drawer>.
import "./../components/general/app-layout";
import notFoundTemplate from "./../components/general/not-found/template.html";

/**
 * View — guest-app shell controller.
 *
 * Renders a single <app-layout> host. When logged in it contains the scrolling
 * page-content slot, the floating bottom tab bar and the slide-in menu drawer;
 * when logged out it is an empty host the full-screen auth states render into.
 * After every state change it refreshes the active tab + chrome visibility from
 * the state's `getActiveTab()` / `showsBottomNav()` hooks.
 */
export class View extends IF.View
{
    constructor( appInstance )
    {
        super( appInstance );
    }

    init()
    {
        this.render( this.app.container, '<app-layout></app-layout>' );

        this.app.addEventListener( IF.CustomEvents.TYPE.AFTER_STATE_CHANGE, () =>
        {
            this.updateChrome();
            this.closeMenu();
            this.closeWeather();
            if ( this.pageContent )
            {
                this.pageContent.scrollTo( { top: 0, behavior: "instant" } );
            }
        } );

        if ( this.app.user )
        {
            this.switchToInApp();
        }
        else
        {
            this.switchToAuth();
        }
    }

    /**
     * The 404 page: the subdomain names no house we may show — unknown, or a
     * draft opened without a preview token (App.init). Rendered in place of
     * the whole shell, in the same device frame.
     */
    showNotFound( slug )
    {
        this.render( this.app.container, '<div id="main-wrapper" class="ga-root"></div>' );
        this.render( this.app.container.querySelector( "#main-wrapper" ), notFoundTemplate, {
            host : slug ? window.location.host : ""
        } );
        document.title = _lcs( "Seite nicht gefunden" ) + " · BELTURO";
    }

    /**
     * A small note above everything while a draft is open through "Gäste-App
     * ansehen" — so the host knows guests do not see this yet. Outside the
     * shell, which re-renders on every auth switch.
     */
    showPreviewNote()
    {
        if ( document.querySelector( ".preview-note" ) )
        {
            return;
        }
        const note = document.createElement( "div" );
        note.className = "preview-note";
        note.setAttribute( "role", "status" );
        note.innerHTML = '<i class="icon icon--eye-off"></i><span></span>';
        note.querySelector( "span" ).textContent = _lcs( "Vorschau · Entwurf — für Gäste noch nicht sichtbar" );
        document.body.appendChild( note );
    }

    switchToInApp()
    {
        this.applayout = this.app.container.querySelector( "app-layout" );
        if ( this.applayout )
        {
            this.applayout.render();
            this.pageContent = this.app.container.querySelector( 'section[data-id="page-content"]' );
            this.bottomNav   = this.app.container.querySelector( "bottom-nav" );
            this.topMenu     = this.app.container.querySelector( "top-menu" );
            this.menuDrawer  = this.app.container.querySelector( "menu-drawer" );
            this.weatherSheet = this.app.container.querySelector( "weather-sheet" );
            this.updateChrome();
        }
    }

    switchToAuth()
    {
        this.applayout = this.app.container.querySelector( "app-layout" );
        if ( this.applayout )
        {
            this.applayout.render();
        }
        this.pageContent = null;
        this.bottomNav = null;
        this.topMenu = null;
        this.menuDrawer = null;
        this.weatherSheet = null;
    }

    /**
     * Sync the floating chrome (tab bar + menu button) with the active state.
     * Detail / sub-page / auth states hide both; tab states light their tab.
     *
     * One flag drives both because they are one layer: a full-bleed detail
     * screen that kept the menu button would have two round buttons in its top
     * corners doing unrelated things.
     */
    updateChrome()
    {
        const state = this.app.stateManager.currentState;
        if ( !this.bottomNav || !state )
        {
            return;
        }

        const shows = typeof state.showsBottomNav === "function" ? state.showsBottomNav() : true;
        this.bottomNav.setHidden( !shows );
        this.topMenu && this.topMenu.setHidden( !shows );

        const tab = typeof state.getActiveTab === "function" ? state.getActiveTab() : null;
        this.bottomNav.setActive( tab );

        // The bar is re-rendered when the shell switches in and out of the
        // in-app layout, which resets the dot — repaint it from what the chat
        // last knew rather than waiting for the next poll.
        this.setChatUnread( this.app.chat?.hasUnread === true );

        // ...and ask again, because a navigation is the moment a guest would
        // notice. The background timer alone runs once a minute, which is fine
        // for someone standing still and far too slow for someone moving
        // between tabs. One tiny request per navigation buys a dot that appears
        // when they look. Not while the chat itself is open — that screen polls
        // the whole thread and owns the unread state for as long as it is up.
        if ( tab !== "chat" )
        {
            this.app.chat?.refreshUnread();
        }
    }

    /**
     * The unread dot on the Chat tab.
     *
     * Called by core/Chat whenever the answer changes, and again after every
     * navigation (the bar outlives a screen but not a shell switch). Safe
     * before the bar exists — the auth shell has no tabs.
     */
    setChatUnread( hasUnread )
    {
        this.bottomNav && this.bottomNav.setUnread( "chat", hasUnread );
    }

    openMenu()
    {
        this.menuDrawer && this.menuDrawer.open();
    }

    closeMenu()
    {
        this.menuDrawer && this.menuDrawer.close();
    }

    /**
     * Weather sheet (opened from the home screen).
     * @param {('hours'|'forecast')} [mode='forecast'] Which view to open on.
     */
    openWeather( mode = "forecast" )
    {
        this.weatherSheet && this.weatherSheet.open( mode );
    }

    closeWeather()
    {
        this.weatherSheet && this.weatherSheet.close();
    }

    /**
     * Generic toast notification. Vanilla (no runtime dependency) — styled via
     * toast.css on the BELTURO tokens, tinted by currentColor icons.
     *
     * @param {string} message
     * @param {('success'|'error'|'info'|'warning')} [type='success']
     * @param {number} [duration=3200]
     * @returns {HTMLElement}
     */
    toast( message, type = "success", duration = 3200 )
    {
        const ICONS = {
            success : "check",
            error   : "triangle-alert",
            warning : "triangle-alert",
            info    : "bell"
        };

        const container = this._ensureToastContainer();

        const el = document.createElement( "div" );
        el.className = "toast toast--" + type;
        el.setAttribute( "role", type === "error" ? "alert" : "status" );

        const icon = document.createElement( "i" );
        icon.className = "icon icon--" + ( ICONS[ type ] || ICONS.info );

        const msg = document.createElement( "span" );
        msg.className = "toast__msg";
        msg.textContent = message;

        el.append( icon, msg );
        container.appendChild( el );

        requestAnimationFrame( () => el.classList.add( "toast--in" ) );

        let removed = false;
        const remove = () =>
        {
            if ( removed ) return;
            removed = true;
            clearTimeout( timer );
            el.classList.remove( "toast--in" );
            el.addEventListener( "transitionend", () => el.remove(), { once: true } );
            setTimeout( () => el.remove(), 400 );
        };

        const timer = setTimeout( remove, duration );
        el.addEventListener( "click", remove );

        return el;
    }

    /**
     * A standing banner above the tab bar — for a state rather than an event:
     * "offline", "a new version is ready", "install the app". Unlike a toast it
     * stays until it is acted on, dismissed, or removed by its owner; unlike a
     * notice it never blocks the screen.
     *
     * Inside the device frame, stacked when there are several. Text goes in via
     * textContent.
     *
     * @param {Object} opts
     * @param {string} opts.icon
     * @param {string} opts.text
     * @param {string} [opts.actionLabel]  a button; the banner closes when tapped
     * @param {Function} [opts.onAction]
     * @param {Function} [opts.onDismiss]  the guest closed it with ×
     * @param {("default"|"quiet")} [opts.tone]
     * @param {boolean} [opts.dismissible=true]
     * @returns {{ el: HTMLElement, remove: Function }}
     */
    banner( { icon = "info", text, actionLabel = null, onAction = null, onDismiss = null, tone = "default", dismissible = true } = {} )
    {
        const frame = this.app.container.querySelector( ".ga-root" ) || document.body;
        let stack = frame.querySelector( ".ga-banners" );
        if ( !stack )
        {
            stack = document.createElement( "div" );
            stack.className = "ga-banners";
            frame.appendChild( stack );
        }

        const el = document.createElement( "div" );
        el.className = "ga-banner ga-banner--" + tone;
        el.setAttribute( "role", "status" );

        const glyph = document.createElement( "i" );
        glyph.className = "icon icon--" + icon + " ga-banner__icon";
        const msg = document.createElement( "span" );
        msg.className = "ga-banner__text";
        msg.textContent = text || "";
        el.append( glyph, msg );

        let removed = false;
        const remove = () =>
        {
            if ( removed ) return;
            removed = true;
            el.classList.remove( "ga-banner--in" );
            setTimeout( () => el.remove(), 260 );
        };

        if ( actionLabel )
        {
            const action = document.createElement( "button" );
            action.type = "button";
            action.className = "ga-banner__action";
            action.textContent = actionLabel;
            action.addEventListener( "click", () =>
            {
                remove();
                onAction && onAction();
            } );
            el.appendChild( action );
        }

        if ( dismissible )
        {
            const close = document.createElement( "button" );
            close.type = "button";
            close.className = "ga-banner__close";
            close.setAttribute( "aria-label", _lcs( "Schließen" ) );
            close.innerHTML = '<i class="icon icon--x"></i>';
            close.addEventListener( "click", () =>
            {
                remove();
                onDismiss && onDismiss();
            } );
            el.appendChild( close );
        }

        stack.appendChild( el );
        requestAnimationFrame( () => el.classList.add( "ga-banner--in" ) );

        return { el, remove };
    }

    /**
     * A dismissible notice the guest has to acknowledge.
     *
     * Distinct from toast(): a toast is a receipt for something that already
     * happened and is gone in three seconds. This is for something the guest
     * still has to DO — the unvalidated-email prompt after a login — where a
     * message that disappears on its own is a message most people never read.
     *
     * Resolves once dismissed. Escape, the backdrop and the button all close.
     *
     * @returns {Promise<void>}
     */
    notice( { icon = "info", title, text, confirmLabel = _lcs( "Verstanden" ) } = {} )
    {
        return new Promise( ( resolve ) =>
        {
            const overlay = document.createElement( "div" );
            overlay.className = "notice";
            overlay.innerHTML =
                '<div class="notice__backdrop" data-id="backdrop"></div>' +
                '<div class="notice__dialog" role="alertdialog" aria-modal="true">' +
                    '<span class="notice__icon"><i class="icon"></i></span>' +
                    '<h2 class="notice__title"></h2>' +
                    '<p class="notice__text"></p>' +
                    '<button class="btn btn--primary btn--lg btn--block" type="button" data-id="ok"></button>' +
                '</div>';

            overlay.querySelector( ".notice__icon .icon" ).className = "icon icon--" + icon;
            overlay.querySelector( ".notice__title" ).textContent = title || "";
            overlay.querySelector( ".notice__text" ).textContent = text || "";
            overlay.querySelector( '[data-id="ok"]' ).textContent = confirmLabel;

            // Inside the device frame, so it is bounded by the phone mock on a
            // desktop instead of covering the whole browser window.
            ( this.app.container.querySelector( ".ga-root" ) || document.body ).appendChild( overlay );
            requestAnimationFrame( () => overlay.classList.add( "notice--in" ) );

            let settled = false;
            const close = () =>
            {
                if ( settled ) return;
                settled = true;
                document.removeEventListener( "keydown", onKey );
                overlay.classList.remove( "notice--in" );
                setTimeout( () => { overlay.remove(); resolve(); }, 220 );
            };
            const onKey = ( e ) => { if ( e.key === "Escape" ) close(); };

            overlay.querySelector( '[data-id="ok"]' ).addEventListener( "click", close );
            overlay.querySelector( '[data-id="backdrop"]' ).addEventListener( "click", close );
            document.addEventListener( "keydown", onKey );

            overlay.querySelector( '[data-id="ok"]' ).focus();
        } );
    }

    /**
     * A yes/no the guest has to answer before something irreversible happens.
     *
     * Distinct from notice(): a notice is acknowledged and resolves however it
     * was dismissed, which is right for "we sent you a mail" and wrong for
     * "delete this". Here Escape and the backdrop mean NO — dismissing a
     * question is not answering it, and the destructive branch must be reached
     * only by pressing the destructive button.
     *
     * @returns {Promise<boolean>}
     */
    confirm( { icon = "circle-help", title, text,
               confirmLabel = _lcs( "Löschen" ), cancelLabel = _lcs( "Abbrechen" ), danger = true } = {} )
    {
        return new Promise( ( resolve ) =>
        {
            const overlay = document.createElement( "div" );
            overlay.className = "notice";
            overlay.innerHTML =
                '<div class="notice__backdrop" data-id="backdrop"></div>' +
                '<div class="notice__dialog" role="alertdialog" aria-modal="true">' +
                    '<span class="notice__icon"><i class="icon"></i></span>' +
                    '<h2 class="notice__title"></h2>' +
                    '<p class="notice__text"></p>' +
                    '<button class="btn btn--lg btn--block" type="button" data-id="ok"></button>' +
                    '<button class="btn btn--secondary btn--lg btn--block notice__cancel" type="button" data-id="cancel"></button>' +
                '</div>';

            overlay.querySelector( ".notice__icon .icon" ).className = "icon icon--" + icon;
            overlay.querySelector( ".notice__title" ).textContent = title || "";
            overlay.querySelector( ".notice__text" ).textContent = text || "";

            const ok = overlay.querySelector( '[data-id="ok"]' );
            ok.textContent = confirmLabel;
            ok.classList.add( danger ? "btn--danger" : "btn--primary" );

            const cancel = overlay.querySelector( '[data-id="cancel"]' );
            cancel.textContent = cancelLabel;

            ( this.app.container.querySelector( ".ga-root" ) || document.body ).appendChild( overlay );
            requestAnimationFrame( () => overlay.classList.add( "notice--in" ) );

            let settled = false;
            const close = ( answer ) =>
            {
                if ( settled ) return;
                settled = true;
                document.removeEventListener( "keydown", onKey );
                overlay.classList.remove( "notice--in" );
                setTimeout( () => { overlay.remove(); resolve( answer ); }, 220 );
            };
            const onKey = ( e ) => { if ( e.key === "Escape" ) close( false ); };

            ok.addEventListener( "click", () => close( true ) );
            cancel.addEventListener( "click", () => close( false ) );
            overlay.querySelector( '[data-id="backdrop"]' ).addEventListener( "click", () => close( false ) );
            document.addEventListener( "keydown", onKey );

            // Focus lands on the safe answer, so a stray Enter cancels.
            cancel.focus();
        } );
    }

    _ensureToastContainer()
    {
        let container = document.querySelector( ".toast-container" );
        if ( !container )
        {
            container = document.createElement( "div" );
            container.className = "toast-container";
            document.body.appendChild( container );
        }
        return container;
    }
}
